<template>
  <div class="product-view-container" :class="{ 'fullscreen-mode': isFullscreen }">
    <!-- Header -->
    <div class="table-header">
      <div class="header-left">
        <div class="icon-wrapper">
          <v-icon color="#023449" size="22">mdi-package-variant</v-icon>
        </div>
        <div>
          <h3 class="table-title">Análise por Produtos</h3>
          <p class="table-subtitle">
            {{ filteredProducts.length }} produto{{ filteredProducts.length !== 1 ? 's' : '' }} •
            {{ totalOcorrencias }} ocorrências
          </p>
        </div>
      </div>
      <div class="table-controls">
        <v-btn
            small
            outlined
            color="#023449"
            @click="toggleFullscreen"
            class="action-btn"
        >
          <v-icon left small>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
          {{ isFullscreen ? 'Sair Tela Cheia' : 'Tela Cheia' }}
        </v-btn>
        <v-btn
            small
            outlined
            color="#023449"
            @click="expandAll"
            class="action-btn"
            :disabled="loading"
        >
          <v-icon left small>mdi-arrow-expand-vertical</v-icon>
          Expandir Todos
        </v-btn>
        <v-btn
            small
            outlined
            color="#023449"
            @click="collapseAll"
            class="action-btn"
            :disabled="loading"
        >
          <v-icon left small>mdi-arrow-collapse-vertical</v-icon>
          Recolher Todos
        </v-btn>
      </div>
    </div>

    <!-- Tables Card -->
    <v-card class="table-card" elevation="2">
      <v-card-text class="pa-0">
        <div v-if="loading" class="table-loading">
          <v-progress-circular indeterminate color="#023449" size="48"></v-progress-circular>
          <p>Carregando dados...</p>
        </div>

        <div v-else-if="filteredProducts.length === 0" class="table-empty">
          <v-icon size="64" color="#cbd5e1">mdi-inbox</v-icon>
          <h3>Nenhum produto encontrado</h3>
          <p>Ajuste os filtros ou busca para visualizar dados</p>
        </div>

        <div v-else class="table-wrapper">
          <!-- NÍVEL 1: Produtos -->
          <table class="data-table">
            <thead class="level-1-header">
            <tr>
              <th style="width: 50px;"></th>
              <th class="sortable-header" @click="sortTable('codigo')">
                <div class="header-content">
                  <span>Código</span>
                  <v-icon small :color="isSortActive('codigo') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('codigo') }}
                  </v-icon>
                </div>
              </th>
              <th class="sortable-header" @click="sortTable('descricao')">
                <div class="header-content">
                  <span>Descrição</span>
                  <v-icon small :color="isSortActive('descricao') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('descricao') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('ocorrenciasDivergentes')">
                <div class="header-content">
                  <span>Ocorrências</span>
                  <v-icon small :color="isSortActive('ocorrenciasDivergentes') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('ocorrenciasDivergentes') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('totalQuantidadeEstoque')">
                <div class="header-content">
                  <span>Qtd Estoque</span>
                  <v-icon small :color="isSortActive('totalQuantidadeEstoque') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('totalQuantidadeEstoque') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('totalQuantidadeContada')">
                <div class="header-content">
                  <span>Qtd Contada</span>
                  <v-icon small :color="isSortActive('totalQuantidadeContada') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('totalQuantidadeContada') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('totalDiferencaQuantidade')">
                <div class="header-content">
                  <span>Diff Quantidade</span>
                  <v-icon small :color="isSortActive('totalDiferencaQuantidade') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('totalDiferencaQuantidade') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-right sortable-header" @click="sortTable('totalValorEstoque')">
                <div class="header-content">
                  <span>Valor Estoque</span>
                  <v-icon small :color="isSortActive('totalValorEstoque') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('totalValorEstoque') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-right sortable-header" @click="sortTable('totalValorContado')">
                <div class="header-content">
                  <span>Valor Contado</span>
                  <v-icon small :color="isSortActive('totalValorContado') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('totalValorContado') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-right sortable-header" @click="sortTable('totalDiferencaValor')">
                <div class="header-content">
                  <span>Diff Valor</span>
                  <v-icon small :color="isSortActive('totalDiferencaValor') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('totalDiferencaValor') }}
                  </v-icon>
                </div>
              </th>
            </tr>
            </thead>
            <tbody>
            <template v-for="produto in paginatedData">
              <!-- Linha do Produto (Nível 1) -->
              <tr
                  :key="`produto-${produto.codigo}`"
                  class="level-1-row"
                  @click="toggleProduto(produto.codigo)"
              >
                <td>
                  <v-btn icon x-small color="#023449">
                    <v-icon small>
                      {{ expandedProdutos.includes(produto.codigo) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                    </v-icon>
                  </v-btn>
                </td>
                <td>
                  <span class="produto-codigo">{{ produto.codigo }}</span>
                </td>
                <td>
                  <div class="cell-content">
                    <v-icon small color="#023449" class="mr-2">mdi-package-variant</v-icon>
                    <v-tooltip bottom color="#023449" content-class="tooltip-no-wrap">
                      <template v-slot:activator="{ on, attrs }">
                        <span
                            class="text-truncate font-weight-medium text-caption"
                            v-bind="attrs"
                            v-on="on"
                        >
                          {{ produto.descricao }}
                        </span>
                      </template>
                      <span class="white--text">{{ produto.descricao }}</span>
                    </v-tooltip>
                  </div>
                </td>
                <td class="text-center">
                  <v-chip small color="#e0f2fe" text-color="#0c4a6e" class="font-weight-bold">
                    {{ produto.ocorrenciasDivergentes }} / {{ produto.ocorrencias }}
                  </v-chip>
                </td>
                <td class="text-center">
                  <span class="qty-value">{{ formatQuantity(produto.totalQuantidadeEstoque) }}</span>
                </td>
                <td class="text-center">
                  <span class="qty-value">{{ formatQuantity(produto.totalQuantidadeContada) }}</span>
                </td>
                <td class="text-center">
                  <span :class="['diff-value', getDiffClass(produto.totalDiferencaQuantidade)]">
                    {{ formatQuantity(produto.totalDiferencaQuantidade) }}
                  </span>
                </td>
                <td class="text-right">
                  {{ formatCurrency(produto.totalValorEstoque) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(produto.totalValorContado) }}
                </td>
                <td class="text-right">
                  <span :class="['diff-value', getDiffClass(produto.totalDiferencaValor)]">
                    {{ formatCurrency(produto.totalDiferencaValor) }}
                  </span>
                </td>
              </tr>

              <!-- NÍVEL 2: Detalhes por Filial/Data -->
              <template v-if="expandedProdutos.includes(produto.codigo)">
                <tr :key="`detalhes-${produto.codigo}`" class="level-2-header-row">
                  <td colspan="10">
                    <table class="nested-table level-2-table">
                      <thead>
                      <tr>
                        <th style="width: 120px;" class="sortable-header" @click="sortTableLevel2(produto.codigo, 'filialCodigo')">
                          <div class="header-content">
                            <span>Código Filial</span>
                            <v-icon x-small :color="isSortActiveLevel2(produto.codigo, 'filialCodigo') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(produto.codigo, 'filialCodigo') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="sortable-header" @click="sortTableLevel2(produto.codigo, 'filialNome')">
                          <div class="header-content">
                            <span>Filial</span>
                            <v-icon x-small :color="isSortActiveLevel2(produto.codigo, 'filialNome') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(produto.codigo, 'filialNome') }}
                            </v-icon>
                          </div>
                        </th>
                        <th style="width: 120px;" class="sortable-header" @click="sortTableLevel2(produto.codigo, 'data')">
                          <div class="header-content">
                            <span>Data</span>
                            <v-icon x-small :color="isSortActiveLevel2(produto.codigo, 'data') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(produto.codigo, 'data') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-center sortable-header" @click="sortTableLevel2(produto.codigo, 'quantidadeEstoque')">
                          <div class="header-content">
                            <span>Qtd Estoque</span>
                            <v-icon x-small :color="isSortActiveLevel2(produto.codigo, 'quantidadeEstoque') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(produto.codigo, 'quantidadeEstoque') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-center sortable-header" @click="sortTableLevel2(produto.codigo, 'quantidadeContada')">
                          <div class="header-content">
                            <span>Qtd Contada</span>
                            <v-icon x-small :color="isSortActiveLevel2(produto.codigo, 'quantidadeContada') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(produto.codigo, 'quantidadeContada') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-center sortable-header" @click="sortTableLevel2(produto.codigo, 'diferencaQuantidade')">
                          <div class="header-content">
                            <span>Diff Qtd</span>
                            <v-icon x-small :color="isSortActiveLevel2(produto.codigo, 'diferencaQuantidade') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(produto.codigo, 'diferencaQuantidade') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-right sortable-header" @click="sortTableLevel2(produto.codigo, 'valorEstoque')">
                          <div class="header-content">
                            <span>Valor Estoque</span>
                            <v-icon x-small :color="isSortActiveLevel2(produto.codigo, 'valorEstoque') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(produto.codigo, 'valorEstoque') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-right sortable-header" @click="sortTableLevel2(produto.codigo, 'valorContado')">
                          <div class="header-content">
                            <span>Valor Contado</span>
                            <v-icon x-small :color="isSortActiveLevel2(produto.codigo, 'valorContado') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(produto.codigo, 'valorContado') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-right sortable-header" @click="sortTableLevel2(produto.codigo, 'diferencaValor')">
                          <div class="header-content">
                            <span>Diff Valor</span>
                            <v-icon x-small :color="isSortActiveLevel2(produto.codigo, 'diferencaValor') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(produto.codigo, 'diferencaValor') }}
                            </v-icon>
                          </div>
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr
                          v-for="detalhe in produto.detalhes"
                          :key="`detalhe-${produto.codigo}-${detalhe.filialCodigo}-${detalhe.data}`"
                          class="level-2-row"
                      >
                        <td>
                          <span class="filial-badge">{{ detalhe.filialCodigo }}</span>
                        </td>
                        <td>
                          <div class="cell-content">
                            <v-icon x-small color="#64748b" class="mr-2">mdi-office-building</v-icon>
                            <span>{{ detalhe.filialNome }}</span>
                          </div>
                        </td>
                        <td>
                          <div class="cell-content">
                            <v-icon x-small color="#64748b" class="mr-2">mdi-calendar</v-icon>
                            <span>{{ formatDate(detalhe.data) }}</span>
                          </div>
                        </td>
                        <td class="text-center">
                          <span class="qty-value">{{ formatQuantity(detalhe.quantidadeEstoque) }}</span>
                        </td>
                        <td class="text-center">
                          <span class="qty-value">{{ formatQuantity(detalhe.quantidadeContada) }}</span>
                        </td>
                        <td class="text-center">
                          <span :class="['diff-value', getDiffClass(detalhe.diferencaQuantidade)]">
                            {{ formatQuantity(detalhe.diferencaQuantidade) }}
                          </span>
                        </td>
                        <td class="text-right">{{ formatCurrency(detalhe.valorEstoque) }}</td>
                        <td class="text-right">{{ formatCurrency(detalhe.valorContado) }}</td>
                        <td class="text-right">
                          <span :class="['diff-value', getDiffClass(detalhe.diferencaValor)]">
                            {{ formatCurrency(detalhe.diferencaValor) }}
                          </span>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </template>
            </tbody>

            <!-- Linha Totais -->
            <tfoot v-if="filteredProducts.length > 0" class="table-footer">
            <tr class="totals-row">
              <td></td>
              <td colspan="2">
                <div class="cell-content">
                  <v-icon small color="#023449" class="mr-2">mdi-sigma</v-icon>
                  <strong>TOTAL</strong>
                </div>
              </td>
              <td class="text-center">
                <v-chip small color="#e0f2fe" text-color="#0c4a6e" class="font-weight-bold">
                  {{ totals.ocorrenciasDivergentes }} / {{ totals.ocorrencias }}
                </v-chip>
              </td>
              <td class="text-center">
                <strong>{{ formatQuantity(totals.totalQuantidadeEstoque) }}</strong>
              </td>
              <td class="text-center">
                <strong>{{ formatQuantity(totals.totalQuantidadeContada) }}</strong>
              </td>
              <td class="text-center">
                <span :class="['diff-value', 'total-value', getDiffClass(totals.totalDiferencaQuantidade)]">
                  {{ formatQuantity(totals.totalDiferencaQuantidade) }}
                </span>
              </td>
              <td class="text-right">
                <strong>{{ formatCurrency(totals.totalValorEstoque) }}</strong>
              </td>
              <td class="text-right">
                <strong>{{ formatCurrency(totals.totalValorContado) }}</strong>
              </td>
              <td class="text-right">
                <span :class="['diff-value', 'total-value', getDiffClass(totals.totalDiferencaValor)]">
                  {{ formatCurrency(totals.totalDiferencaValor) }}
                </span>
              </td>
            </tr>
            </tfoot>
          </table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Pagination -->
<!--    <div class="pagination" v-if="!loading && !isFullscreen && filteredProducts.length > 0">-->
    <div class="pagination" v-if="!loading && filteredProducts.length > 0">
      <div class="pagination-left">
        <div class="pagination-info">
          <v-icon small color="#64748b" class="mr-2">mdi-information</v-icon>
          <template v-if="itemsPerPage === -1">
            Mostrando <strong>{{ filteredProducts.length }}</strong> produto{{ filteredProducts.length !== 1 ? 's' : '' }}
          </template>
          <template v-else>
            Mostrando
            <strong>{{ startIndex + 1 }}</strong> - <strong>{{ endIndex }}</strong>
            de <strong>{{ filteredProducts.length }}</strong> produtos
          </template>
        </div>
        <div class="items-per-page">
          <span class="items-label">Itens por página:</span>
          <v-select
              :items="itemsPerPageOptions"
              v-model="itemsPerPage"
              @change="changeItemsPerPage"
              dense
              outlined
              hide-details
              item-text="text"
              item-value="value"
              class="items-select"
          ></v-select>
        </div>
      </div>
      <div class="pagination-controls" v-if="itemsPerPage !== -1">
        <v-btn icon small :disabled="currentPage === 1" @click="goToPage(1)" color="#023449">
          <v-icon small>mdi-page-first</v-icon>
        </v-btn>
        <v-btn text small :disabled="currentPage === 1" @click="previousPage" color="#023449">
          <v-icon left small>mdi-chevron-left</v-icon>
          Anterior
        </v-btn>
        <div class="page-numbers">
          <v-btn
              v-for="page in visiblePages"
              :key="page"
              small
              :outlined="page !== currentPage"
              :color="page === currentPage ? '#023449' : ''"
              :dark="page === currentPage"
              @click="goToPage(page)"
              class="page-btn"
          >
            {{ page }}
          </v-btn>
        </div>
        <v-btn text small :disabled="currentPage === totalPages" @click="nextPage" color="#023449">
          Próxima
          <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn icon small :disabled="currentPage === totalPages" @click="goToPage(totalPages)" color="#023449">
          <v-icon small>mdi-page-last</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProdutosView',

  props: {
    data: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currentPage: 1,
      itemsPerPage: 20,
      itemsPerPageOptions: [
        { text: '10', value: 10 },
        { text: '20', value: 20 },
        { text: '50', value: 50 },
        { text: '100', value: 100 },
        // { text: 'Todos', value: -1 }
      ],
      expandedProdutos: [],
      sortBy: 'ocorrenciasDivergentes',
      sortOrder: 'desc',
      isFullscreen: false,
      // Ordenação por produto (nível 2)
      sortByProduto: {}
    };
  },

  computed: {
    productsData() {
      const productsMap = new Map();

      this.data.forEach(filial => {
        if (filial.dias) {
          filial.dias.forEach(dia => {
            if (dia.produtos && dia.produtos.length > 0) {
              dia.produtos.forEach(produto => {
                if (!productsMap.has(produto.codigo)) {
                  productsMap.set(produto.codigo, {
                    codigo: produto.codigo,
                    descricao: produto.descricao,
                    ocorrencias: 0,
                    ocorrenciasDivergentes: 0,
                    totalQuantidadeEstoque: 0,
                    totalQuantidadeContada: 0,
                    totalDiferencaQuantidade: 0,
                    totalValorEstoque: 0,
                    totalValorContado: 0,
                    totalDiferencaValor: 0,
                    detalhes: []
                  });
                }

                const produtoAgrupado = productsMap.get(produto.codigo);
                produtoAgrupado.ocorrencias++;

                // Verificar se há divergência
                if (produto.diferencaQuantidade !== 0 || produto.diferencaValor !== 0) {
                  produtoAgrupado.ocorrenciasDivergentes++;
                }

                produtoAgrupado.totalQuantidadeEstoque += produto.quantidadeEstoque || 0;
                produtoAgrupado.totalQuantidadeContada += produto.quantidadeContada || 0;
                produtoAgrupado.totalDiferencaQuantidade += produto.diferencaQuantidade || 0;
                produtoAgrupado.totalValorEstoque += produto.valorEstoque || 0;
                produtoAgrupado.totalValorContado += produto.valorContado || 0;
                produtoAgrupado.totalDiferencaValor += produto.diferencaValor || 0;

                produtoAgrupado.detalhes.push({
                  filialCodigo: filial.codigo,
                  filialNome: filial.empresa,
                  data: dia.data,
                  quantidadeEstoque: produto.quantidadeEstoque,
                  quantidadeContada: produto.quantidadeContada,
                  diferencaQuantidade: produto.diferencaQuantidade,
                  valorEstoque: produto.valorEstoque,
                  valorContado: produto.valorContado,
                  diferencaValor: produto.diferencaValor
                });
              });
            }
          });
        }
      });

      return Array.from(productsMap.values());
    },

    filteredProducts() {
      let products = [...this.productsData];

      // Aplicar ordenação nos detalhes de cada produto (nível 2)
      products = products.map(produto => {
        let sortedProduto = { ...produto };

        if (produto.detalhes) {
          // Se já existe ordenação customizada para este produto, usa ela
          if (this.sortByProduto[produto.codigo]) {
            const sortConfig = this.sortByProduto[produto.codigo];
            sortedProduto.detalhes = this.sortDetalhes([...produto.detalhes], sortConfig.field, sortConfig.order);
          } else {
            // Ordenação padrão: diferencaValor crescente
            sortedProduto.detalhes = this.sortDetalhes([...produto.detalhes], 'diferencaValor', 'asc');
          }
        }

        return sortedProduto;
      });

      // Aplicar ordenação principal (nível 1)
      if (this.sortBy) {
        products.sort((a, b) => {
          let valueA = this.getSortValue(a, this.sortBy);
          let valueB = this.getSortValue(b, this.sortBy);

          if (valueA === null || valueA === undefined) return 1;
          if (valueB === null || valueB === undefined) return -1;

          if (valueA < valueB) return this.sortOrder === 'asc' ? -1 : 1;
          if (valueA > valueB) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return products;
    },

    totalOcorrencias() {
      return this.filteredProducts.reduce((sum, p) => sum + p.ocorrencias, 0);
    },

    totalPages() {
      if (this.itemsPerPage === -1) return 1;
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    },

    startIndex() {
      if (this.itemsPerPage === -1) return 0;
      return (this.currentPage - 1) * this.itemsPerPage;
    },

    endIndex() {
      if (this.itemsPerPage === -1) return this.filteredProducts.length;
      const end = this.startIndex + this.itemsPerPage;
      return end > this.filteredProducts.length ? this.filteredProducts.length : end;
    },

    paginatedData() {
      // if (this.isFullscreen) return this.filteredProducts;
      // if (this.itemsPerPage === -1) return this.filteredProducts;
      return this.filteredProducts.slice(this.startIndex, this.endIndex);
    },

    totals() {
      return {
        ocorrencias: this.filteredProducts.reduce((sum, p) => sum + p.ocorrencias, 0),
        ocorrenciasDivergentes: this.filteredProducts.reduce((sum, p) => sum + p.ocorrenciasDivergentes, 0),
        totalQuantidadeEstoque: this.filteredProducts.reduce((sum, p) => sum + p.totalQuantidadeEstoque, 0),
        totalQuantidadeContada: this.filteredProducts.reduce((sum, p) => sum + p.totalQuantidadeContada, 0),
        totalDiferencaQuantidade: this.filteredProducts.reduce((sum, p) => sum + p.totalDiferencaQuantidade, 0),
        totalValorEstoque: this.filteredProducts.reduce((sum, p) => sum + p.totalValorEstoque, 0),
        totalValorContado: this.filteredProducts.reduce((sum, p) => sum + p.totalValorContado, 0),
        totalDiferencaValor: this.filteredProducts.reduce((sum, p) => sum + p.totalDiferencaValor, 0)
      };
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },

  methods: {
    toggleProduto(codigo) {
      const index = this.expandedProdutos.indexOf(codigo);
      if (index > -1) {
        this.expandedProdutos.splice(index, 1);
      } else {
        this.expandedProdutos.push(codigo);
      }
    },

    expandAll() {
      this.expandedProdutos = this.paginatedData.map(p => p.codigo);
    },

    collapseAll() {
      this.expandedProdutos = [];
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value || 0);
    },

    formatDiff(value) {
      const val = value || 0;
      return val >= 0 ? `+${val}` : val;
    },

    formatQuantity(value) {
      const val = value || 0;
      // Remove casas decimais se for número inteiro
      if (Number.isInteger(val)) {
        return val.toLocaleString('pt-BR');
      }
      // Formata com no máximo 2 casas decimais
      return val.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },

    getDiffClass(value) {
      const val = value || 0;
      if (val === 0) return 'neutral';
      return val < 0 ? 'negative' : 'positive';
    },

    sortTable(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = field;
        this.sortOrder = 'asc';
      }
      this.currentPage = 1;
    },

    getSortValue(item, field) {
      return item[field];
    },

    getSortIcon(field) {
      if (this.sortBy !== field) return 'mdi-sort';
      return this.sortOrder === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending';
    },

    isSortActive(field) {
      return this.sortBy === field;
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    goToPage(page) {
      this.currentPage = page;
    },

    changeItemsPerPage(value) {
      this.itemsPerPage = value;
      this.currentPage = 1;
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      document.body.style.overflow = this.isFullscreen ? 'hidden' : '';
    },

    // Ordenação Nível 2
    sortTableLevel2(produtoCodigo, field) {
      if (!this.sortByProduto[produtoCodigo] || this.sortByProduto[produtoCodigo].field !== field) {
        this.$set(this.sortByProduto, produtoCodigo, { field, order: 'asc' });
      } else {
        const currentOrder = this.sortByProduto[produtoCodigo].order;
        this.$set(this.sortByProduto, produtoCodigo, { field, order: currentOrder === 'asc' ? 'desc' : 'asc' });
      }
    },

    sortDetalhes(detalhes, field, order) {
      return detalhes.sort((a, b) => {
        let valueA = this.getDetalheSortValue(a, field);
        let valueB = this.getDetalheSortValue(b, field);

        if (valueA === null || valueA === undefined) return 1;
        if (valueB === null || valueB === undefined) return -1;

        if (valueA < valueB) return order === 'asc' ? -1 : 1;
        if (valueA > valueB) return order === 'asc' ? 1 : -1;
        return 0;
      });
    },

    getDetalheSortValue(detalhe, field) {
      const fieldMap = {
        'filialCodigo': parseInt(detalhe.filialCodigo) || 0,
        'filialNome': detalhe.filialNome,
        'data': detalhe.data,
        'quantidadeEstoque': detalhe.quantidadeEstoque,
        'quantidadeContada': detalhe.quantidadeContada,
        'diferencaQuantidade': detalhe.diferencaQuantidade,
        'valorEstoque': detalhe.valorEstoque,
        'valorContado': detalhe.valorContado,
        'diferencaValor': detalhe.diferencaValor
      };
      return fieldMap[field];
    },

    getSortIconLevel2(produtoCodigo, field) {
      if (!this.sortByProduto[produtoCodigo] || this.sortByProduto[produtoCodigo].field !== field) {
        return 'mdi-sort';
      }
      return this.sortByProduto[produtoCodigo].order === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending';
    },

    isSortActiveLevel2(produtoCodigo, field) {
      return this.sortByProduto[produtoCodigo] && this.sortByProduto[produtoCodigo].field === field;
    }
  },

  beforeDestroy() {
    if (this.isFullscreen) {
      document.body.style.overflow = '';
    }
  }
};
</script>

<style scoped>
.product-view-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.3s ease;
}

.product-view-container.fullscreen-mode {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: #f8fafc;
  z-index: 9999;
  padding: 20px;
  overflow: hidden;
  gap: 20px;
  display: flex;
  flex-direction: column;
}

.fullscreen-mode .table-header {
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.08);
}

.fullscreen-mode .table-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.fullscreen-mode .table-card .v-card__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  position: relative;
}

.fullscreen-mode .table-wrapper {
  flex: 1;
  overflow: auto;
  min-height: 0;
  position: relative;
}

.fullscreen-mode .level-1-header {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(2, 52, 73, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  background: #ffffff;
  padding: 20px 24px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-wrapper {
  background: linear-gradient(135deg, rgba(2, 52, 73, 0.08) 0%, rgba(2, 52, 73, 0.04) 100%);
  border-radius: 10px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table-title {
  margin: 0;
  color: #023449;
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.table-subtitle {
  margin: 4px 0 0 0;
  color: #64748b;
  font-size: 0.813rem;
  font-weight: 500;
}

.table-controls {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

.table-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
}

.table-loading,
.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
  padding: 40px;
}

.table-loading p {
  margin: 0;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
}

.table-empty h3 {
  margin: 0;
  color: #023449;
  font-size: 1.25rem;
  font-weight: 700;
}

.table-empty p {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.nested-table {
  width: 100%;
  border-collapse: collapse;
}

.level-1-header {
  background: #023449;
}

.level-1-header th {
  padding: 16px;
  text-align: left;
  font-weight: 700;
  font-size: 0.75rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
}

.table-footer {
  background: #f1f5f9;
  border-top: 3px solid #023449;
}

.totals-row {
  font-weight: 700;
  background: #f8fafc;
}

.totals-row td {
  padding: 16px;
  font-size: 0.875rem;
  color: #023449;
  vertical-align: middle;
  border-top: 2px solid #cbd5e1;
}

.totals-row .cell-content {
  font-size: 0.938rem;
  font-weight: 790;
  color: #023449;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.totals-row .total-value {
  font-size: 0.938rem;
  font-weight: 790;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.sortable-header:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.sortable-header .header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sortable-header.text-center .header-content,
.sortable-header.text-right .header-content {
  justify-content: center;
}

.sortable-header .header-content span {
  flex: 1;
}

.sortable-header .header-content .v-icon {
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.sortable-header:hover .header-content .v-icon {
  opacity: 1 !important;
  color: #ffffff !important;
}

.level-1-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 2px solid #e2e8f0;
}

.level-1-row:hover {
  background-color: #f8fafc;
}

.level-1-row td {
  padding: 16px;
  font-size: 0.875rem;
  color: #374151;
  vertical-align: middle;
  white-space: nowrap;
}

.level-1-row td:nth-child(3) {
  /* Coluna da descrição */
  white-space: normal;
  max-width: 350px;
}

.level-2-header-row {
  background: #f8fafc;
}

.level-2-table {
  margin: 0;
  background: #f8fafc;
}

.level-2-table thead {
  background: #cbd5e1;
}

.level-2-table thead th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  color: #023449;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.level-2-table .sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.level-2-table .sortable-header:hover {
  background-color: rgba(2, 52, 73, 0.08);
}

.level-2-table .sortable-header .header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.level-2-table .sortable-header.text-center .header-content,
.level-2-table .sortable-header.text-right .header-content {
  justify-content: center;
}

.level-2-table .sortable-header .header-content .v-icon {
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.level-2-table .sortable-header:hover .header-content .v-icon {
  opacity: 1 !important;
  color: #023449 !important;
}

.level-2-row {
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #e2e8f0;
}

.level-2-row:hover {
  background-color: #ffffff;
}

.level-2-row td {
  padding: 12px 16px;
  font-size: 0.813rem;
  color: #475569;
}

.cell-content {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 300px;
  overflow: hidden;
}

.cell-content .v-icon {
  flex-shrink: 0;
}

.cell-content .v-tooltip {
  min-width: 0;
  overflow: hidden;
  flex: 1;
}

.produto-codigo {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  color: #023449;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.813rem;
}

.filial-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #dbeafe;
  border-radius: 6px;
  color: #1e40af;
  font-weight: 700;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;
}

.qty-value {
  font-weight: 600;
  color: #334155;
  font-size: 0.813rem;
}

.diff-value {
  font-weight: 700;
  font-size: 0.813rem;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
  white-space: nowrap;
}

.diff-value.positive {
  background: #d1fae5;
  color: #047857;
}

.diff-value.negative {
  background: #fecaca;
  color: #991b1b;
}

.diff-value.neutral {
  background: #f3f4f6;
  color: #64748b;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
}

.pagination-left {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
}

.pagination-info strong {
  color: #023449;
  font-weight: 700;
  margin: 0 4px;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 12px;
}

.items-label {
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.items-select {
  max-width: 90px;
  font-weight: 600;
}

.items-select >>> .v-input__control {
  min-height: 36px !important;
}

.items-select >>> .v-select__selections {
  padding: 0 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #023449;
}

.items-select >>> .v-input__slot {
  min-height: 36px !important;
  padding: 0 8px !important;
}

.items-select >>> fieldset {
  border-color: #cbd5e1;
}

.items-select >>> .v-select__selection {
  margin: 0 !important;
}

.pagination-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  min-width: 40px !important;
}

/* Responsive */
@media (max-width: 1200px) {
  .table-controls {
    flex-wrap: wrap;
  }
}

@media (max-width: 1024px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .table-controls {
    width: 100%;
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .table-wrapper {
    overflow-x: auto;
  }

  .data-table {
    min-width: 1200px;
  }

  .pagination {
    flex-direction: column;
    padding: 16px;
    align-items: stretch;
  }

  .pagination-left {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .items-per-page {
    width: 100%;
    justify-content: space-between;
  }

  .items-select {
    max-width: 100px;
  }

  .pagination-controls {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }
}

@media (max-width: 600px) {
  .table-header,
  .pagination {
    padding: 16px;
  }

  .icon-wrapper {
    padding: 8px;
  }

  .table-title {
    font-size: 1rem;
  }

  .table-subtitle {
    font-size: 0.75rem;
  }

  .page-btn {
    min-width: 36px !important;
  }

  .level-1-header th,
  .level-2-table thead th {
    font-size: 0.688rem;
    padding: 12px 8px;
  }

  .level-1-row td,
  .level-2-row td {
    padding: 10px 8px;
    font-size: 0.75rem;
  }
}

.table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: rgba(2, 52, 73, 0.3);
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(2, 52, 73, 0.5);
}

@media (prefers-reduced-motion: reduce) {
  .level-1-row,
  .level-2-row {
    transition: none !important;
  }
}

/* Tooltip customizado */
.tooltip-no-wrap {
  white-space: nowrap !important;
  max-width: none !important;
}
</style>